import {
  createAsyncThunk,
  createSlice,
  type PayloadAction,
} from "@reduxjs/toolkit";
import mealApi from "../../../api/meal/mealApi";
import type {
  MealCompletePickupData,
  MealCompletePickupPayload,
  MealMonitoringDetailData,
  MealMonitoringDetailPayload,
} from "../../../api/meal/types/mealMonitoringDetailType";
import {
  isRequestCancelled,
  type RejectPayload,
  toRejectPayload,
} from "../../../helpers/ApiError";
import type { RootState } from "../../store";

interface FetchMealMonitoringDetailPayload {
  uuid: string;
  payload: MealMonitoringDetailPayload;
}

interface MealMonitoringDetailState {
  detail: MealMonitoringDetailData | null;
  completePickup: MealCompletePickupData | null;

  isLoadingDetail: boolean;
  isCompletingPickup: boolean;

  detailError: string | null;
  completePickupError: string | null;

  detailRequestId: string | null;
  completePickupRequestId: string | null;
}

const initialState: MealMonitoringDetailState = {
  detail: null,
  completePickup: null,

  isLoadingDetail: false,
  isCompletingPickup: false,

  detailError: null,
  completePickupError: null,

  detailRequestId: null,
  completePickupRequestId: null,
};

export const fetchMealMonitoringDetail = createAsyncThunk<
  MealMonitoringDetailData,
  FetchMealMonitoringDetailPayload,
  { rejectValue: RejectPayload }
>(
  "mealMonitoringDetail/fetchMealMonitoringDetail",
  async ({ uuid, payload }, { rejectWithValue, signal }) => {
    try {
      const response = await mealApi.getMealMonitoringDetail(
        uuid,
        payload,
        signal,
      );

      return response.data;
    } catch (err) {
      if (isRequestCancelled(err, signal)) {
        throw err;
      }

      return rejectWithValue(
        toRejectPayload(err, "Gagal mengambil detail meal"),
      );
    }
  },
);

export const completeMealPickup = createAsyncThunk<
  MealCompletePickupData,
  MealCompletePickupPayload,
  { rejectValue: RejectPayload }
>(
  "mealMonitoringDetail/completeMealPickup",
  async (payload, { rejectWithValue, signal }) => {
    try {
      const response = await mealApi.mealCompletePickupMenu(payload, signal);

      return response.data;
    } catch (err) {
      if (isRequestCancelled(err, signal)) {
        throw err;
      }

      return rejectWithValue(
        toRejectPayload(err, "Gagal menyelesaikan pengambilan meal"),
      );
    }
  },
);

const mealMonitoringDetailSlice = createSlice({
  name: "mealMonitoringDetail",
  initialState,
  reducers: {
    setMealMonitoringDetail: (
      state,
      action: PayloadAction<MealMonitoringDetailData>,
    ) => {
      state.detail = action.payload;
      state.detailError = null;
    },

    clearMealMonitoringDetail: (state) => {
      state.detail = null;
      state.detailError = null;
      state.detailRequestId = null;
      state.isLoadingDetail = false;
    },

    clearCompletePickup: (state) => {
      state.completePickup = null;
      state.completePickupError = null;
      state.completePickupRequestId = null;
      state.isCompletingPickup = false;
    },

    clearMealMonitoringDetailError: (state) => {
      state.detailError = null;
    },

    clearCompletePickupError: (state) => {
      state.completePickupError = null;
    },

    resetMealMonitoringDetail: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchMealMonitoringDetail.pending, (state, action) => {
        state.isLoadingDetail = true;
        state.detailError = null;
        state.detailRequestId = action.meta.requestId;
      })

      .addCase(fetchMealMonitoringDetail.fulfilled, (state, action) => {
        if (state.detailRequestId !== action.meta.requestId) {
          return;
        }

        state.isLoadingDetail = false;
        state.detail = action.payload;
        state.detailError = null;
        state.detailRequestId = null;
      })

      .addCase(fetchMealMonitoringDetail.rejected, (state, action) => {
        if (state.detailRequestId !== action.meta.requestId) {
          return;
        }

        state.isLoadingDetail = false;
        state.detailRequestId = null;

        if (action.meta.aborted) {
          state.detailError = null;
          return;
        }

        state.detailError =
          action.payload?.message ?? "Gagal mengambil detail meal";
      })

      .addCase(completeMealPickup.pending, (state, action) => {
        state.isCompletingPickup = true;
        state.completePickup = null;
        state.completePickupError = null;
        state.completePickupRequestId = action.meta.requestId;
      })

      .addCase(completeMealPickup.fulfilled, (state, action) => {
        if (state.completePickupRequestId !== action.meta.requestId) {
          return;
        }

        state.isCompletingPickup = false;
        state.completePickup = action.payload;
        state.completePickupError = null;
        state.completePickupRequestId = null;
      })

      .addCase(completeMealPickup.rejected, (state, action) => {
        if (state.completePickupRequestId !== action.meta.requestId) {
          return;
        }

        state.isCompletingPickup = false;
        state.completePickupRequestId = null;

        if (action.meta.aborted) {
          state.completePickupError = null;
          return;
        }

        state.completePickupError =
          action.payload?.message ?? "Gagal menyelesaikan pengambilan meal";
      });
  },
});

export const {
  setMealMonitoringDetail,
  clearMealMonitoringDetail,
  clearCompletePickup,
  clearMealMonitoringDetailError,
  clearCompletePickupError,
  resetMealMonitoringDetail,
} = mealMonitoringDetailSlice.actions;

export const selectMealMonitoringDetail = (state: RootState) =>
  state.mealMonitoringDetailReducer.detail;

export const selectCompletePickup = (state: RootState) =>
  state.mealMonitoringDetailReducer.completePickup;

export const selectIsLoadingMealMonitoringDetail = (state: RootState) =>
  state.mealMonitoringDetailReducer.isLoadingDetail;

export const selectIsCompletingMealPickup = (state: RootState) =>
  state.mealMonitoringDetailReducer.isCompletingPickup;

export const selectMealMonitoringDetailError = (state: RootState) =>
  state.mealMonitoringDetailReducer.detailError;

export const selectCompletePickupError = (state: RootState) =>
  state.mealMonitoringDetailReducer.completePickupError;

export default mealMonitoringDetailSlice.reducer;
